import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Button, Row, Col, DatePicker } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { IReferralLetter } from "../../interfaces/IReferral";
import dayjs from "dayjs";

interface ReferralEditModalProps {
  open: boolean;
  form: any;
  schemeSelected: any;
  onCancel: () => void;
  showServices: any;
  setServices: any;
  services: any;
  setPatientReferralForm: any;
  referral: any;
}

const ReferralEditModal: React.FC<ReferralEditModalProps> = ({
  open,
  form,
  schemeSelected,
  onCancel,
  showServices,
  setServices,
  services,
  setPatientReferralForm,
  referral,
}) => {
  const isCGHS = schemeSelected?.toLowerCase()?.includes("cghs");
  const dateFormat = "DD-MM-YYYY";

  const handleOk = () => {
    onCancel();
  };

  const [servicesState, setServicesState] = useState(services);

  useEffect(() => {
    setServicesState(services);
  }, [services]);

  const handleServiceChange = (index: any, field: any, value: any) => {
    const updatedServices = [...servicesState];
    updatedServices[index] = { ...updatedServices[index], [field]: value };
    setServicesState(updatedServices);
    setServices(updatedServices); // Update the parent component's state
    // Update form field
    if (field === "validUpto") {
      form.setFieldsValue({ [`services[${index}].${field}`]: value });
    }
  };

  const handleAddService = () => {
    const newService = {
      referralType: "",
      name: "",
      remarks: "",
      validUpto: "",
      maxQty: "",
    };

    setServicesState((prevServices: any) => [...prevServices, newService]);

    // Also update the form fields for the new service
    const currentServices = form.getFieldValue("services") || [];
    form.setFieldsValue({
      services: [...currentServices, newService],
    });
  };

  useEffect(() => {
    if (referral) {
      const servicesData = constructServices(referral);
      setServices(servicesData);

      const updatedValues = {
        ...referral,
        services: servicesData,
        dateIssued:
          referral?.dateIssued &&
          referral?.dateIssued !== "" &&
          dayjs(referral?.dateIssued, dateFormat, true).isValid()
            ? dayjs(referral?.dateIssued, dateFormat)
            : null,
        referralExpiryFrom:
          referral?.referralExpiryFrom &&
          dayjs(referral?.referralExpiryFrom, dateFormat, true).isValid()
            ? dayjs(referral?.referralExpiryFrom, dateFormat)
            : null,
        referralExpiryTo:
          referral?.referralExpiryTo &&
          dayjs(referral?.referralExpiryTo, dateFormat, true).isValid()
            ? dayjs(referral?.referralExpiryTo, dateFormat)
            : null,
      };

      form.setFieldsValue(updatedValues);
    }
  }, [referral, form]);

  useEffect(() => {
    setPatientReferralForm(form);
  }, [form]);

  // Handler to remove a service
  const handleRemoveService = (index: any) => {
    setServicesState((prevServices: any) =>
      prevServices.filter((_: any, i: any) => i !== index),
    );

    const currentServices = form.getFieldValue("services") || [];
    const updatedServices = currentServices?.filter(
      (_: any, i: any) => i !== index,
    );
    form.setFieldsValue({
      services: updatedServices,
    });
  };

  return (
    <Modal
      title="Edit Referral"
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      okText="Save"
      width={600}
      //   bodyStyle={{ padding: "12px" }}
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
          <div className="floating-wrapper">
              <label className="floating-static-label">
                  Referral ID <span style={{ color: "red" }}>*</span>
                </label>
            <Form.Item
              name={isCGHS ? "refferenceID" : "referralNo"}
              rules={[{ required: true, message: "Referral ID required" }]}
            >
                <Input
                  style={{ fontSize: "10px" }}
                  placeholder=" "
                  className="input-field"
                />
            </Form.Item>
            </div>
          </Col>

          <Col span={12}>
          <div className="floating-wrapper">
              <label className="floating-static-label">
                  {isCGHS ? "Beneficiary ID" : "Service Number"}{" "}
                  <span style={{ color: "red" }}>*</span>
                </label>
            <Form.Item
              //   label={isCGHS ? "Beneficiary ID" : "Service Number"}
              name={isCGHS ? "beneficiaryId" : "serviceNumber"}
              rules={[{ required: true, message: "Required" }]}
            >
                <Input
                  style={{ fontSize: "10px" }}
                  placeholder=" "
                  className="input-field"
                />
            </Form.Item>
            </div>
          </Col>
        </Row>

        <Row gutter={[4, 4]}>
          <Col span={8}>
          <div className="floating-wrapper">
              <label className="floating-static-label">
                  Referral Type <span style={{ color: "red" }}></span>
                </label>
            <Form.Item
              //   label="Referral Type"
              name={isCGHS ? "cardType" : "referralType"}
            >
                <Input
                  style={{ fontSize: "10px" }}
                  placeholder=" "
                  className="input-field"
                />
            </Form.Item>
            </div>
          </Col>

          <Col span={8}>
            <div className="floating-wrapper">
              <label className="floating-static-label">
                Issued Date <span style={{ color: "red" }}>*</span>
              </label>
              <Form.Item
                //   label="Issued Date"
                name={isCGHS ? "dateIssued" : "referralExpiryFrom"}
                rules={[{ required: true, message: "Required" }]}
              >
                <DatePicker
                  format={dateFormat}
                  style={{ width: "100%", fontSize: "10px" }}
                  className="input-field"
                />
              </Form.Item>
            </div>
          </Col>

          <Col span={8}>
            <div className="floating-wrapper">
              <label className="floating-static-label">
                Valid Upto <span style={{ color: "red" }}></span>
              </label>
              <Form.Item
                // label="Valid Upto"
                name={"referralExpiryTo"}
                rules={[{ required: true, message: "Required" }]}
              >
                <DatePicker
                  format={dateFormat}
                  style={{ width: "100%", fontSize: "10px" }}
                  className="input-field"
                />
              </Form.Item>
            </div>
          </Col>
        </Row>

        {isCGHS &&
          servicesState &&
          servicesState?.length > 0 &&
          showServices && (
            <div
              style={{
                maxHeight: 300,
                overflowY: "auto",
                marginBottom: "0.8rem",
                padding: "4px",
              }}
            >
              {servicesState?.map((item: any, index: any) => {
                return (
                  <Row
                    gutter={[4, 4]}
                    key={index}
                    style={{
                      border: "1px solid lightgray",
                      borderRadius: "12px",
                      padding: "4px",
                      marginBottom: "0.4rem",
                      width: "100%",
                    }}
                  >
                    <Col span={2}>
                      <Form.Item
                        style={{
                          background: "#00549E",
                          color: "white",
                          borderRadius: "50%",
                          padding: "0 4px",
                          fontSize: "10px",
                          height: "26px",
                          width: "20px",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        {index + 1}
                      </Form.Item>
                    </Col>
                    <Col xs={21} sm={21} md={7} lg={7} xl={7}>
                      <Form.Item name={["services", index, "name"]}>
                        <div className="floating-input">
                          <Input
                            value={item.name}
                            onChange={(e) =>
                              handleServiceChange(index, "name", e.target.value)
                            }
                            className="input-field"
                            style={{
                              border: !item?.remarks ? "1px solid red" : "",
                              fontSize: "10px",
                            }}
                          />
                          <label className="floating-label">
                            Name <span style={{ color: "red" }}>*</span>
                          </label>
                        </div>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={2} lg={2} xl={2}>
                      <div className="floating-wrapper">
                        <label className="floating-static-label">
                          Valid Upto <span style={{ color: "red" }}>*</span>
                        </label>
                        <Form.Item
                          name={["services", index, "validUpto"]}
                          // label="VALID UPTO"
                        >
                          <DatePicker
                            value={item?.validUpto}
                            className="input-field"
                            onChange={(date) =>
                              handleServiceChange(index, "validUpto", date)
                            }
                            format={dateFormat}
                            style={{
                              width: "100%",
                              border: !item?.validUpto ? "1px solid red" : "",
                              fontSize: "10px",
                            }}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                    <Col xs={10} sm={10} md={7} lg={7} xl={7}>
                      <Form.Item name={["services", index, "referralType"]}>
                        <div className="floating-input">
                          <Input
                            value={item.referralType}
                            onChange={(e) =>
                              handleServiceChange(
                                index,
                                "referralType",
                                e.target.value,
                              )
                            }
                            className="input-field"
                            style={{
                              border: !item?.remarks ? "1px solid red" : "",
                              fontSize: "10px",
                            }}
                          />
                          <label className="floating-label">
                            referral Type{" "}
                            <span style={{ color: "red" }}>*</span>
                          </label>
                        </div>
                      </Form.Item>
                    </Col>
                    <Col xs={8} sm={8} md={2} lg={2} xl={2}>
                      <Form.Item
                        name={["services", index, "maxQty"]}
                        initialValue={item.maxQty}
                      >
                        <div className="floating-input">
                          <Input
                            value={item.maxQty}
                            onChange={(e) =>
                              handleServiceChange(
                                index,
                                "maxQty",
                                e.target.value,
                              )
                            }
                            className="input-field"
                            style={{
                              border: !item?.maxQty ? "1px solid red" : "",
                              fontSize: "10px",
                            }}
                          />
                          <label className="floating-label">
                            MAX QUANTITY <span style={{ color: "red" }}>*</span>
                          </label>
                        </div>
                      </Form.Item>
                    </Col>
                    <Col
                      xs={6}
                      sm={6}
                      md={1}
                      lg={1}
                      xl={1}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        // marginTop: "30px",
                      }}
                    >
                      {servicesState?.length > 1 && (
                        <Button
                          type="text"
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => handleRemoveService(index)}
                          style={{
                            border: "none",
                            boxShadow: "none",
                          }}
                        />
                      )}
                    </Col>
                  </Row>
                );
              })}
            </div>
          )}
        {showServices && (
          <Row style={{ marginBottom: "1rem" }}>
            <Col span={24}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Button
                  type="dashed"
                  onClick={handleAddService}
                  icon={<PlusOutlined />}
                  style={{
                    backgroundColor: "rgb(0, 76, 143)",
                    color: "#fff",
                    boxShadow: "0px 2px 8px 0px #00000040",
                    width: "100%",
                    fontSize: "10px",
                  }}
                >
                  <span style={{ fontSize: "10px" }}>Add Service</span>
                </Button>
              </div>
            </Col>
          </Row>
        )}
      </Form>
    </Modal>
  );
};

export default ReferralEditModal;

const constructServices = (referral: IReferralLetter | null) => {
  if (!referral) return [];
  const services = referral?.services ?? referral?.referralServicesResponses;

  return services?.map((service: any) => ({
    id: service?.id,
    referralType: service?.referralType,
    name: service?.name,
    remarks: service?.remarks,
    validUpto:
      service.validUpto &&
      service.validUpto !== "" &&
      dayjs(service?.validUpto, "DD-MM-YYYY", true).isValid()
        ? dayjs(service.validUpto, "DD-MM-YYYY")
        : null,
    maxQty: service?.maxQty,
  }));
};
